import React, { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { usePermissions } from "../Auth/PermissionsContext";
import { useRealtimeEvent } from "./RealtimeProvider";

/**
 * Quand les droits du compte connecté changent (un administrateur modifie ses rôles, ou la
 * définition d'un rôle qu'il porte), l'interface se recharge toute seule : le jeu de permissions est
 * chargé une fois par session, sans ça la personne garderait les anciens menus et boutons (et
 * verrait des 403) jusqu'à un rafraîchissement manuel. Le serveur diffuse `permissions-changed`
 * (scope "user" + email, ou scope "role" + roleKey) ; on ne réagit qu'à ce qui nous concerne.
 */

export type PermissionsChangedPayload = {
	scope?: "user" | "role" | string;
	email?: string | null;
	roleKey?: string | null;
	roles?: string[] | null;
	change?: string | null;
	by?: string | null;
};

export const RELOAD_DELAY_MS = 2500;

/** Vrai si l'événement concerne le compte décrit (e-mail ou rôle porté). Pur, testable. */
export function concernsMe(payload: PermissionsChangedPayload | null | undefined, myEmail: string | null | undefined, myRoles: string[]): boolean {
	if (!payload) return false;
	const email = (myEmail ?? "").trim().toLowerCase();
	if (payload.scope === "user") {
		const target = (payload.email ?? "").trim().toLowerCase();
		return !!email && !!target && email === target;
	}
	if (payload.scope === "role") {
		const key = (payload.roleKey ?? "").trim().toLowerCase();
		return !!key && myRoles.some((r) => (r ?? "").trim().toLowerCase() === key);
	}
	return false;
}

export const PermissionsChangeWatcher: React.FC<{ reload?: () => void }> = ({ reload }) => {
	const { identity, roles } = usePermissions();
	const armed = useRef(false);
	const timer = useRef<number | null>(null);

	useEffect(() => () => {
		if (timer.current !== null) window.clearTimeout(timer.current);
	}, []);

	useRealtimeEvent("permissions-changed", (payload: PermissionsChangedPayload) => {
		if (armed.current) return;
		if (!concernsMe(payload, identity.email, roles)) return;
		armed.current = true;
		toast.info("Vos droits d'accès viennent d'être modifiés : l'interface se recharge.", { toastId: "permissions-changed", autoClose: RELOAD_DELAY_MS });
		timer.current = window.setTimeout(() => {
			if (reload) reload();
			else window.location.reload();
		}, RELOAD_DELAY_MS);
	});

	return null;
};

export default PermissionsChangeWatcher;
